import React, { useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../constants";
import { ConfirmModal } from "./ConfirmModal";
import { marketingService } from "../services/marketing.service";
import type { MarketingBanner, MarketingBannerType } from "../types";

const BANNER_RULES: Record<
  MarketingBannerType,
  {
    label: string;
    requirement: string;
    width?: number;
    height?: number;
    aspectRatio?: number;
    aspectRatioLabel?: string;
    tolerance?: number;
    usage: string;
  }
> = {
  BANNER_FIXO_HEADER: {
    label: "Banner Fixo Header",
    requirement: "320 x 100 px",
    width: 320,
    height: 100,
    usage: "Topo fixo",
  },
  BANNER_FEED: {
    label: "Banner Feed",
    requirement: "300 x 250 px",
    width: 300,
    height: 250,
    usage: "Feed",
  },
  BANNER_FIXO_FOOTER: {
    label: "Banner Fixo Footer",
    requirement: "320 x 100 px",
    width: 320,
    height: 100,
    usage: "Rodapé fixo",
  },
  SPLASH_SCREEN: {
    label: "Splash Screen",
    requirement: "Proporção 9:16 (ex: 1080 x 1920)",
    aspectRatio: 9 / 16,
    aspectRatioLabel: "9:16",
    tolerance: 0.01,
    usage: "Tela de abertura",
  },
  POPUP: {
    label: "Popup",
    requirement: "300 x 250 px",
    width: 300,
    height: 250,
    usage: "Popup",
  },
};

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
const MAX_FILE_SIZE = 50 * 1024 * 1024;
const SINGLE_INSTANCE_TYPES: MarketingBannerType[] = [
  "BANNER_FIXO_HEADER",
  "BANNER_FIXO_FOOTER",
  "SPLASH_SCREEN",
  "POPUP",
];

export default function MarketingManagement() {
  const [banners, setBanners] = useState<MarketingBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState<MarketingBanner | null>(null);
  const [selectedType, setSelectedType] = useState<MarketingBannerType | "">("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [link, setLink] = useState("");
  const [error, setError] = useState("");
  const [dimensionError, setDimensionError] = useState("");
  const [success, setSuccess] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  const typeCounts = useMemo(() => {
    return banners.reduce<Record<MarketingBannerType, number>>((acc, banner) => {
      acc[banner.type] = (acc[banner.type] || 0) + 1;
      return acc;
    }, {} as Record<MarketingBannerType, number>);
  }, [banners]);

  useEffect(() => {
    loadBanners();
  }, []);

  useEffect(() => {
    if (imageFile && selectedType) {
      validateImageForType(imageFile, selectedType).catch((err) =>
        setDimensionError(err instanceof Error ? err.message : String(err))
      );
    }
  }, [imageFile, selectedType]);

  useEffect(() => {
    return () => {
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
      }
    };
  }, [filePreview]);

  const typeOptions = useMemo(() => {
    return [
      { value: "", label: "Selecione o tipo de banner", disabled: false },
      ...Object.entries(BANNER_RULES).map(([value, rule]) => ({
        value,
        label: `${rule.label} • ${rule.requirement}`,
        disabled: SINGLE_INSTANCE_TYPES.includes(value as MarketingBannerType) &&
          (typeCounts[value as MarketingBannerType] || 0) >= 1,
      })),
    ];
  }, [typeCounts]);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const data = await marketingService.getAll();
      setBanners(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message || "Erro ao carregar banners");
    } finally {
      setLoading(false);
    }
  };

  const validateImageForType = (image: File, type: MarketingBannerType) => {
    const rules = BANNER_RULES[type];
    if (!rules) return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(image);

      img.onload = () => {
        const width = img.width;
        const height = img.height;
        URL.revokeObjectURL(url);

        if (rules.width && rules.height) {
          if (width !== rules.width || height !== rules.height) {
            reject(
              new Error(
                `Dimensão inválida para ${rules.label}. Esperado ${rules.width}x${rules.height}px e recebido ${width}x${height}px.`
              )
            );
            return;
          }
        } else if (rules.aspectRatio) {
          const ratio = width / height;
          const tolerance = rules.tolerance ?? 0.01;
          if (Math.abs(ratio - rules.aspectRatio) > tolerance) {
            reject(
              new Error(
                `Dimensão inválida para ${rules.label}. A proporção deve ser ${rules.aspectRatioLabel ?? rules.aspectRatio.toFixed(2)} e recebido ${width}x${height}px.`
              )
            );
            return;
          }
        }

        resolve();
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Não foi possível ler a imagem selecionada."));
      };

      img.src = url;
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setError("");
    setDimensionError("");

    if (!file) {
      setImageFile(null);
      setFilePreview(null);
      return;
    }

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setDimensionError("Formato inválido. Use JPG, JPEG, PNG ou GIF.");
      setImageFile(null);
      setFilePreview(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setDimensionError("Arquivo muito grande. Tamanho máximo: 50MB.");
      setImageFile(null);
      setFilePreview(null);
      return;
    }

    const preview = URL.createObjectURL(file);
    if (filePreview) {
      URL.revokeObjectURL(filePreview);
    }

    setImageFile(file);
    setFilePreview(preview);

    if (selectedType) {
      try {
        await validateImageForType(file, selectedType);
      } catch (err: any) {
        setDimensionError(err?.message || "Dimensão inválida para o tipo selecionado.");
      }
    }
  };

  const isValidUrl = (value: string) => {
    if (!value) return true;
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setDimensionError("");

    if (!selectedType) {
      setError("Selecione o tipo de banner.");
      return;
    }

    if (
      SINGLE_INSTANCE_TYPES.includes(selectedType) &&
      (typeCounts[selectedType] || 0) >= 1
    ) {
      setError("Já existe um banner cadastrado para esse tipo. Remova o atual antes de criar outro.");
      return;
    }

    if (!imageFile) {
      setError("Selecione uma imagem para continuar.");
      return;
    }

    if (link && !isValidUrl(link.trim())) {
      setError("Informe um link válido (http ou https).");
      return;
    }

    try {
      await validateImageForType(imageFile, selectedType);
    } catch (err: any) {
      setDimensionError(err?.message || "Dimensão inválida para o tipo selecionado.");
      return;
    }

    try {
      setSaveLoading(true);
      await marketingService.create({
        type: selectedType,
        link: link.trim() || null,
        image: imageFile,
      });

      setSuccess("Banner criado com sucesso!");
      setSelectedType("");
      setImageFile(null);
      setLink("");
      setFileInputKey((prev) => prev + 1);
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
      }
      setFilePreview(null);
      setTimeout(() => setSuccess(""), 5000);
      await loadBanners();
    } catch (err: any) {
      setError(err?.message || "Erro ao criar banner");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleRemove = (banner: MarketingBanner) => {
    setBannerToDelete(banner);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!bannerToDelete) return;

    setDeleteLoading(true);
    setError("");

    try {
      await marketingService.remove(bannerToDelete.id);
      setSuccess("Banner removido com sucesso!");
      setShowDeleteModal(false);
      setTimeout(() => setSuccess(""), 5000);
      await loadBanners();
    } catch (err: any) {
      setError(err?.message || "Erro ao remover banner");
    } finally {
      setDeleteLoading(false);
      setBannerToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setBannerToDelete(null);
  };

  const bannerImageUrl = (image: string) => {
    const base = API_BASE_URL.replace(/\/$/, "");
    return `${base}/uploads/marketing/${image}`;
  };

  const formatDate = (value: string) => {
    const date = new Date(value);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div
      style={{
        padding: 32,
        background: "var(--background)",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1200,
          background: "var(--card)",
          padding: 28,
          borderRadius: 16,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: "var(--card-foreground)",
                marginBottom: 6,
              }}
            >
              Gestão de Marketing (Banners)
            </h1>
            <p style={{ color: "var(--muted-foreground)", margin: 0 }}>
              Cadastre os formatos exigidos com validação de dimensões antes do envio.
            </p>
          </div>
        </div>

        {error && (
          <div
            style={{
              background: "var(--destructive)",
              color: "white",
              padding: 12,
              borderRadius: 8,
              marginBottom: 16,
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              background: "linear-gradient(135deg,#10b981,#059669)",
              color: "white",
              padding: 14,
              borderRadius: 8,
              marginBottom: 16,
              textAlign: "center",
              fontWeight: 700,
            }}
          >
            {success}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.05fr 1fr",
            gap: 20,
            alignItems: "start",
          }}
        >
          <div>
            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  color: "var(--muted-foreground)",
                  padding: 40,
                }}
              >
                Carregando banners...
              </div>
            ) : banners.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 32,
                  border: "1px dashed var(--border)",
                  borderRadius: 12,
                  color: "var(--muted-foreground)",
                }}
              >
                Nenhum banner cadastrado ainda.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                  gap: 16,
                }}
              >
                {banners.map((banner) => {
                  const rule = BANNER_RULES[banner.type];
                  return (
                    <div
                      key={banner.id}
                      style={{
                        background: "var(--input)",
                        border: "1px solid var(--border)",
                        borderRadius: 12,
                        padding: 14,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--foreground)" }}>
                            {rule?.label ?? banner.type}
                          </div>
                          <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                            {rule?.requirement} • {rule?.usage}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemove(banner)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "var(--destructive)",
                            cursor: "pointer",
                            fontWeight: 700,
                          }}
                          aria-label="Remover banner"
                        >
                          Remover
                        </button>
                      </div>

                      <div
                        style={{
                          borderRadius: 10,
                          overflow: "hidden",
                          background: "var(--card)",
                          border: "1px solid var(--border)",
                          minHeight: 140,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={bannerImageUrl(banner.image)}
                          alt={rule?.label ?? "Banner"}
                          style={{ width: "100%", maxHeight: 220, objectFit: "contain" }}
                        />
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "var(--muted-foreground)" }}>
                        <span style={{ maxWidth: "60%", wordBreak: "break-word" }}>
                          {banner.link ? banner.link : "Sem link"}
                        </span>
                        <span>{formatDate(banner.createdAt)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div
            style={{
              background: "var(--input)",
              border: "1px solid var(--border)",
              borderRadius: 12,
              padding: 16,
              position: "sticky",
              top: 16,
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12, color: "var(--foreground)" }}>Novo banner</h3>
            <p style={{ marginTop: 0, marginBottom: 12, color: "var(--muted-foreground)", fontSize: 13 }}>
              Tipos disponíveis com tamanhos exigidos. Validação acontece antes do upload.
            </p>

            <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "var(--foreground)" }}>Tipo</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value as MarketingBannerType)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid var(--border)",
                    background: "var(--card)",
                    color: "var(--foreground)",
                  }}
                >
                  {typeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value} disabled={opt.disabled} style={{ color: opt.disabled ? "var(--muted-foreground)" : undefined }}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {selectedType && SINGLE_INSTANCE_TYPES.includes(selectedType) && (typeCounts[selectedType] || 0) >= 1 && (
                  <span style={{ color: "var(--destructive)", fontSize: 12 }}>
                    Já existe um banner deste tipo. Remova o atual para cadastrar outro.
                  </span>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "var(--foreground)" }}>Link (opcional)</label>
                <input
                  type="text"
                  placeholder="https://"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  style={{
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    padding: 12,
                    borderRadius: 10,
                    color: "var(--foreground)",
                  }}
                />
                {link && !isValidUrl(link) && (
                  <span style={{ color: "var(--destructive)", fontSize: 12 }}>
                    Informe um link válido (http ou https).
                  </span>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "var(--foreground)" }}>Imagem</label>
                <input
                  key={fileInputKey}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif"
                  onChange={handleFileChange}
                  style={{
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    padding: 10,
                    borderRadius: 10,
                    color: "var(--foreground)",
                  }}
                />
                <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                  Formatos: JPG, JPEG, PNG, GIF • Tamanho máx.: 50MB
                </span>
                {dimensionError && (
                  <span style={{ color: "var(--destructive)", fontSize: 12 }}>{dimensionError}</span>
                )}
              </div>

              {filePreview && (
                <div
                  style={{
                    border: "1px dashed var(--border)",
                    borderRadius: 10,
                    padding: 10,
                    background: "var(--card)",
                  }}
                >
                  <p style={{ margin: "0 0 8px", color: "var(--muted-foreground)", fontSize: 13 }}>Pré-visualização</p>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <img
                      src={filePreview}
                      alt="Pré-visualização do banner"
                      style={{ maxWidth: "100%", maxHeight: 160, objectFit: "contain", borderRadius: 8 }}
                    />
                    <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                      <div>{imageFile?.name}</div>
                      <div>{imageFile ? `${(imageFile.size / 1024 / 1024).toFixed(1)} MB` : ""}</div>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={saveLoading}
                style={{
                  background: saveLoading ? "var(--muted)" : "var(--accent)",
                  color: "white",
                  padding: "12px 16px",
                  borderRadius: 10,
                  border: "none",
                  cursor: saveLoading ? "not-allowed" : "pointer",
                  fontWeight: 700,
                }}
              >
                {saveLoading ? "Salvando..." : "Salvar banner"}
              </button>
            </form>

            <div style={{ marginTop: 14 }}>
              <h4 style={{ margin: "10px 0 6px", color: "var(--foreground)" }}>Especificações exigidas</h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 8,
                }}
              >
                {Object.entries(BANNER_RULES).map(([value, rule]) => (
                  <div
                    key={value}
                    style={{
                      border: "1px dashed var(--border)",
                      borderRadius: 10,
                      padding: 10,
                      background: "var(--card)",
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "var(--foreground)", marginBottom: 4 }}>
                      {rule.label}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>{rule.requirement}</div>
                    <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>{rule.usage}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Remover banner"
        message="Tem certeza que deseja remover este banner? Esta ação não pode ser desfeita."
        confirmText="Remover"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        variant="danger"
      />
    </div>
  );
}
